import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { readStoredAdminSession } from '../admin/adminUtils';
import {
  authSessionGetHeaders,
  authSessionGetUrl,
  clearLoginGuard,
  clearStoredSessionToken,
  fetchSessionProfile,
  isLoginGuardActive,
  parseJsonResponse,
  postLoginRequest,
  readStoredSessionToken,
  setLoginGuardMs,
  writeStoredSessionToken,
} from './authSessionApi';
import { trackLogin, trackSignUp } from '../analytics';
import { customerApiErrorMessage } from '../storefront/customerFacingErrors';

const AuthContext = createContext(null);

async function authApiPost(token, payload) {
  const t = String(token || '').trim();
  let r;
  try {
    r = await fetch('/api/admin_auth.php', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Admin-Session': t,
        Authorization: `Bearer ${t}`,
      },
      body: JSON.stringify({ session_token: t, ...payload }),
    });
  } catch {
    return { ok: false, error: customerApiErrorMessage('', 'Could not reach the server. Please try again.'), status: 0 };
  }
  let d = {};
  try {
    d = await r.json();
  } catch {
    return { ok: false, error: 'Invalid response from server.', status: r.status };
  }
  if (!r.ok || !d.success) {
    return { ok: false, error: customerApiErrorMessage(d.error, 'Request failed.'), status: r.status, data: d };
  }
  return { ok: true, data: d, status: r.status };
}

function profileFromPayload(d) {
  return {
    displayName: d?.display_name ? String(d.display_name) : '',
    email: d?.email ? String(d.email) : '',
    phone: d?.phone ? String(d.phone) : '',
  };
}

function authedState(token, d) {
  const role = typeof d?.role === 'string' && d.role ? d.role : 'member';
  const profile = profileFromPayload(d);
  return {
    status: 'authed',
    token,
    username: String(d?.username || ''),
    role,
    displayName: profile.displayName,
    email: profile.email,
    phone: profile.phone,
  };
}

const GUEST_STATE = {
  status: 'guest',
  token: '',
  username: '',
  role: '',
  displayName: '',
  email: '',
  phone: '',
};

export function AuthProvider({ children }) {
  const [state, setState] = useState({
    status: 'loading',
    token: '',
    username: '',
    role: '',
    displayName: '',
    email: '',
    phone: '',
  });
  const refreshAbortRef = useRef(null);
  /** Bumped on login/logout/guest reset so in-flight session checks cannot overwrite a newer session. */
  const sessionGenRef = useRef(0);
  const settleLoadingAuth = useCallback((gen) => {
    if (sessionGenRef.current !== gen) return;
    setState((prev) => (prev.status === 'loading' ? GUEST_STATE : prev));
  }, []);

  const applyGuest = useCallback(() => {
    sessionGenRef.current += 1;
    clearLoginGuard();
    refreshAbortRef.current?.abort();
    refreshAbortRef.current = null;
    clearStoredSessionToken();
    setState(GUEST_STATE);
  }, []);

  /** Only clear storage/state if the token we validated is still the active one (avoids login races). */
  const clearStaleSession = useCallback((checkedToken) => {
    if (isLoginGuardActive()) return;
    const checked = String(checkedToken || '').trim();
    const current = String(readStoredSessionToken() || '').trim();
    if (checked && current !== checked) return;
    clearStoredSessionToken();
    setState((prev) => (prev.status === 'authed' ? prev : GUEST_STATE));
  }, []);

  const refreshAuth = useCallback(async () => {
    const gen = sessionGenRef.current;
    refreshAbortRef.current?.abort();
    const controller = new AbortController();
    refreshAbortRef.current = controller;

    const t = String(readStoredSessionToken() || '').trim();
    if (!t) {
      if (sessionGenRef.current === gen) setState(GUEST_STATE);
      return;
    }
    const timeoutId = window.setTimeout(() => controller.abort(), 12000);
    try {
      const r = await fetch(authSessionGetUrl(t), {
        headers: authSessionGetHeaders(t),
        signal: controller.signal,
        cache: 'no-store',
      });
      if (sessionGenRef.current !== gen || controller.signal.aborted) {
        settleLoadingAuth(gen);
        return;
      }
      const parsed = await parseJsonResponse(r);
      if (!parsed.ok) {
        if (sessionGenRef.current === gen) clearStaleSession(t);
        settleLoadingAuth(gen);
        return;
      }
      const d = parsed.data || {};
      if (!r.ok || !d.logged_in) {
        if (sessionGenRef.current === gen) clearStaleSession(t);
        settleLoadingAuth(gen);
        return;
      }
      if (sessionGenRef.current !== gen) return;
      if (String(readStoredSessionToken() || '').trim() !== t) return;
      clearLoginGuard();
      setState(authedState(t, d));
    } catch (err) {
      if (sessionGenRef.current !== gen || controller.signal.aborted) {
        settleLoadingAuth(gen);
        return;
      }
      clearStaleSession(t);
      settleLoadingAuth(gen);
    } finally {
      window.clearTimeout(timeoutId);
      if (refreshAbortRef.current === controller) {
        refreshAbortRef.current = null;
      }
    }
  }, [clearStaleSession, settleLoadingAuth]);

  const didInitialRefreshRef = useRef(false);
  useEffect(() => {
    if (didInitialRefreshRef.current) return;
    didInitialRefreshRef.current = true;
    refreshAuth();
  }, [refreshAuth]);

  const login = useCallback(async (username, password, remember, nextRaw = '') => {
    const u = String(username || '').trim();
    const p = String(password || '');
    if (!u || !p) return { ok: false, error: 'Enter username or email and password.' };

    refreshAbortRef.current?.abort();
    refreshAbortRef.current = null;

    let posted;
    try {
      posted = await postLoginRequest(u, p);
    } catch {
      return {
        ok: false,
        error: customerApiErrorMessage('', 'Could not reach the sign-in service. Please try again.'),
      };
    }
    if (!posted.ok) {
      return { ok: false, error: customerApiErrorMessage(posted.error, 'Sign-in failed.') };
    }

    const tok = posted.token;
    if (!writeStoredSessionToken(tok, remember)) {
      return {
        ok: false,
        error:
          'Sign-in succeeded but this browser blocked saving the session. Allow site data for this site and try again.',
      };
    }

    setLoginGuardMs(60000);
    sessionGenRef.current += 1;

    const next = authedState(tok, { ...posted.data, username: posted.data?.username || u });
    setState(next);

    if (next.role !== 'admin' && next.role !== 'member') trackLogin('password');
    return {
      ok: true,
      role: next.role,
      username: next.username,
      displayName: next.displayName,
      email: next.email,
      token: tok,
      redirected: false,
    };
  }, []);

  const register = useCallback(async (fields) => {
    let r;
    try {
      r = await fetch('/api/admin_auth.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'register',
          username: String(fields?.username || ''),
          password: String(fields?.password || ''),
          confirm_password: String(fields?.confirmPassword || ''),
          display_name: String(fields?.displayName || ''),
          email: String(fields?.email || ''),
          phone: String(fields?.phone || ''),
        }),
        cache: 'no-store',
      });
    } catch {
      return { ok: false, error: customerApiErrorMessage('', 'Could not reach the server. Please try again.') };
    }
    const parsed = await parseJsonResponse(r);
    if (!parsed.ok) return { ok: false, error: customerApiErrorMessage(parsed.error, 'Registration failed.') };
    const d = parsed.data || {};
    if (!r.ok || !d.success) {
      return { ok: false, error: customerApiErrorMessage(d.error, 'Registration failed.') };
    }
    const tok = String(d.session_token || '').trim();
    if (!tok) return { ok: false, error: 'No session returned.' };
    if (!writeStoredSessionToken(tok, fields?.remember)) {
      return { ok: false, error: 'Account created but session could not be saved in this browser.' };
    }
    setLoginGuardMs(60000);
    sessionGenRef.current += 1;
    const next = authedState(tok, d);
    setState(next);
    if (next.role !== 'admin' && next.role !== 'member') trackSignUp('password');
    return {
      ok: true,
      role: next.role,
      username: next.username,
      displayName: next.displayName,
      email: next.email,
      token: tok,
      redirected: false,
    };
  }, []);

  const updateProfile = useCallback(
    async (displayName, email, phone) => {
      const t = String(state.token || readStoredAdminSession() || '').trim();
      if (!t) return { ok: false, error: 'Sign in again.' };
      const res = await authApiPost(t, {
        action: 'update_profile',
        display_name: String(displayName || ''),
        email: String(email || ''),
        phone: String(phone || ''),
      });
      if (!res.ok) return { ok: false, error: res.error || 'Could not update profile.' };
      setState((prev) =>
        prev.status === 'authed'
          ? {
              ...prev,
              displayName: String(displayName || '').trim(),
              email: String(email || '').trim(),
              phone: String(phone || '').trim(),
            }
          : prev
      );
      return { ok: true };
    },
    [state.token]
  );

  const listOrders = useCallback(async () => {
    const t = String(state.token || readStoredAdminSession() || '').trim();
    if (!t) return { ok: false, error: 'Sign in again.', orders: [] };
    try {
      const r = await fetch('/api/orders.php', {
        headers: {
          'X-Admin-Session': t,
          Authorization: `Bearer ${t}`,
        },
      });
      let d = {};
      try {
        d = await r.json();
      } catch {
        return { ok: false, error: 'Invalid response.', orders: [] };
      }
      if (!r.ok || !d.success) {
        return { ok: false, error: d.error || 'Could not load orders.', orders: [] };
      }
      return { ok: true, orders: Array.isArray(d.orders) ? d.orders : [] };
    } catch {
      return {
        ok: false,
        error: customerApiErrorMessage('', 'Could not reach the server. Please try again.'),
        orders: [],
      };
    }
  }, [state.token]);

  const getOrderDetail = useCallback(
    async (orderNumber) => {
      const t = String(state.token || readStoredAdminSession() || '').trim();
      const num = String(orderNumber || '').trim();
      if (!t || !num) return { ok: false, error: 'Sign in again.', order: null };
      try {
        const r = await fetch(`/api/orders.php?order_number=${encodeURIComponent(num)}`, {
          headers: {
            'X-Admin-Session': t,
            Authorization: `Bearer ${t}`,
          },
        });
        let d = {};
        try {
          d = await r.json();
        } catch {
          return { ok: false, error: 'Invalid response.', order: null };
        }
        if (!r.ok || !d.success) {
          return { ok: false, error: d.error || 'Could not load order.', order: null };
        }
        return { ok: true, order: d.order || null };
      } catch {
        return {
          ok: false,
          error: customerApiErrorMessage('', 'Could not reach the server. Please try again.'),
          order: null,
        };
      }
    },
    [state.token]
  );

  const changePassword = useCallback(
    async (currentPassword, newPassword, confirmPassword) => {
      const t = String(state.token || readStoredAdminSession() || '').trim();
      if (!t) return { ok: false, error: 'Sign in again to change your password.' };
      const res = await authApiPost(t, {
        action: 'change_password',
        current_password: String(currentPassword || ''),
        new_password: String(newPassword || ''),
        confirm_password: String(confirmPassword || ''),
      });
      if (!res.ok) return { ok: false, error: res.error || 'Could not update password.' };
      return { ok: true };
    },
    [state.token]
  );

  const changeUsername = useCallback(
    async (currentPassword, newUsername) => {
      const t = String(state.token || readStoredAdminSession() || '').trim();
      if (!t) return { ok: false, error: 'Sign in again to change your username.' };
      const res = await authApiPost(t, {
        action: 'change_username',
        current_password: String(currentPassword || ''),
        new_username: String(newUsername || ''),
      });
      if (!res.ok) return { ok: false, error: res.error || 'Could not update username.' };
      const uname = String(res.data?.username || newUsername || '').trim();
      setState((prev) =>
        prev.status === 'authed' ? { ...prev, username: uname } : prev
      );
      return { ok: true, username: uname };
    },
    [state.token]
  );

  const listUsers = useCallback(async () => {
    const t = String(state.token || readStoredAdminSession() || '').trim();
    if (!t) return { ok: false, error: 'Sign in again.', users: [] };
    const res = await authApiPost(t, { action: 'list_users' });
    if (!res.ok) return { ok: false, error: res.error || 'Could not load users.', users: [] };
    const users = Array.isArray(res.data?.users) ? res.data.users : [];
    return {
      ok: true,
      users,
      rolesSupported: res.data?.roles_supported !== false,
    };
  }, [state.token]);

  const listCustomers = useCallback(async () => {
    const t = String(state.token || readStoredAdminSession() || '').trim();
    if (!t) return { ok: false, error: 'Sign in again.', customers: [] };
    const res = await authApiPost(t, { action: 'list_customers' });
    if (!res.ok) return { ok: false, error: res.error || 'Could not load customers.', customers: [] };
    const customers = Array.isArray(res.data?.customers) ? res.data.customers : [];
    return {
      ok: true,
      customers,
      rolesSupported: res.data?.roles_supported !== false,
    };
  }, [state.token]);

  const updateUserRole = useCallback(
    async (userId, role) => {
      const t = String(state.token || readStoredAdminSession() || '').trim();
      if (!t) return { ok: false, error: 'Sign in again.' };
      const res = await authApiPost(t, {
        action: 'update_user_role',
        user_id: Number(userId),
        role: String(role || ''),
      });
      if (!res.ok) return { ok: false, error: res.error || 'Could not update role.' };
      return { ok: true, role: String(res.data?.role || role) };
    },
    [state.token]
  );

  const createUser = useCallback(
    async (username, password, confirmPassword, role = 'admin') => {
      const t = String(state.token || readStoredAdminSession() || '').trim();
      if (!t) return { ok: false, error: 'Sign in again.' };
      const res = await authApiPost(t, {
        action: 'create_user',
        username: String(username || ''),
        password: String(password || ''),
        confirm_password: String(confirmPassword || ''),
        role: String(role || 'admin'),
      });
      if (!res.ok) return { ok: false, error: res.error || 'Could not create user.' };
      return {
        ok: true,
        username: String(res.data?.username || username),
        role: String(res.data?.role || role),
      };
    },
    [state.token]
  );

  const resetCustomerPassword = useCallback(
    async (userId, password, confirmPassword) => {
      const t = String(state.token || readStoredAdminSession() || '').trim();
      if (!t) return { ok: false, error: 'Sign in again.' };
      const res = await authApiPost(t, {
        action: 'admin_reset_customer_password',
        user_id: Number(userId),
        password: String(password || ''),
        confirm_password: String(confirmPassword || ''),
      });
      if (!res.ok) return { ok: false, error: res.error || 'Could not reset password.' };
      return {
        ok: true,
        username: String(res.data?.username || ''),
        message: String(res.data?.message || 'Password updated.'),
      };
    },
    [state.token]
  );

  const applySessionToken = useCallback(
    async (token, remember = true) => {
      const tok = String(token || '').trim();
      if (!tok) return { ok: false, error: 'No session token.' };
      refreshAbortRef.current?.abort();
      refreshAbortRef.current = null;
      sessionGenRef.current += 1;
      if (!writeStoredSessionToken(tok, remember)) {
        return { ok: false, error: 'Session could not be saved in this browser.' };
      }
      setLoginGuardMs(60000);
      const verified = await fetchSessionProfile(tok);
      if (!verified.ok) {
        clearStaleSession(tok);
        return { ok: false, error: customerApiErrorMessage(verified.error, 'Session could not be started.') };
      }
      setState(authedState(tok, verified.data));
      clearLoginGuard();
      return { ok: true };
    },
    [clearStaleSession]
  );

  const logout = useCallback(async () => {
    const t = String(state.token || readStoredAdminSession() || '').trim();
    applyGuest();
    if (!t) return;
    try {
      await fetch('/api/admin_auth.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Admin-Session': t,
          Authorization: `Bearer ${t}`,
        },
        body: JSON.stringify({ action: 'logout', session_token: t }),
      });
    } catch {
      /* client already signed out */
    }
  }, [state.token, applyGuest]);

  const value = useMemo(
    () => ({
      ...state,
      ready: state.status !== 'loading',
      isAuthed: state.status === 'authed',
      isAdmin: state.status === 'authed' && state.role === 'admin',
      isCustomer: state.status === 'authed' && state.role === 'customer',
      isStaff:
        state.status === 'authed' && (state.role === 'admin' || state.role === 'member'),
      refreshAuth,
      applySessionToken,
      login,
      register,
      logout,
      changePassword,
      changeUsername,
      updateProfile,
      listUsers,
      listCustomers,
      createUser,
      updateUserRole,
      resetCustomerPassword,
      listOrders,
      getOrderDetail,
    }),
    [
      state,
      refreshAuth,
      applySessionToken,
      login,
      register,
      logout,
      changePassword,
      changeUsername,
      updateProfile,
      listUsers,
      listCustomers,
      createUser,
      updateUserRole,
      resetCustomerPassword,
      listOrders,
      getOrderDetail,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
